// Docker lessons - 30 unique lessons covering beginner to expert

const dockerLessons = [
  // BEGINNER LESSONS (10)
  {
    id: 'docker-001',
    title: 'Check Docker Version',
    description: 'Verify Docker is installed and check its version. This is usually the first command you run to confirm Docker is working.',
    task: 'Display the Docker version installed on your system.',
    hint: 'Use the docker command with a version flag.',
    example: 'docker --help',
    answer: ['docker --version', 'docker -v', 'docker version'],
    tips: [
      'docker --version shows a brief version string',
      'docker version shows detailed client and server info'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker version',
      description: 'Display Docker version information including client and server details.',
      syntax: 'docker version [OPTIONS]',
      options: [
        { flag: '--format, -f', description: 'Format output using a Go template' },
        { flag: '--kubeconfig', description: 'Kubernetes config file' }
      ],
      examples: [
        { code: 'docker --version', description: 'Show brief version string' },
        { code: 'docker version', description: 'Show detailed client and server version info' },
        { code: 'docker version --format "{{.Server.Version}}"', description: 'Show only server version using format' }
      ]
    }
  },
  {
    id: 'docker-002',
    title: 'Pull an Image',
    description: 'Docker images are the blueprints for containers. Pull downloads an image from Docker Hub to your local machine.',
    task: 'Pull the official nginx image from Docker Hub.',
    hint: 'Use docker pull followed by the image name.',
    example: 'docker pull ubuntu',
    answer: ['docker pull nginx'],
    tips: [
      'If no tag is specified, Docker pulls the :latest tag',
      'You can specify versions like nginx:1.21'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker pull',
      description: 'Download an image from a registry (Docker Hub by default).',
      syntax: 'docker pull [OPTIONS] NAME[:TAG|@DIGEST]',
      options: [
        { flag: '-a, --all-tags', description: 'Download all tagged images in the repository' },
        { flag: '--disable-content-trust', description: 'Skip image verification' },
        { flag: '-q, --quiet', description: 'Suppress verbose output' },
        { flag: '--platform', description: 'Set platform (e.g., linux/amd64)' }
      ],
      examples: [
        { code: 'docker pull nginx', description: 'Pull the latest nginx image' },
        { code: 'docker pull nginx:1.21', description: 'Pull a specific version of nginx' },
        { code: 'docker pull -a redis', description: 'Pull all tagged versions of redis' }
      ]
    }
  },
  {
    id: 'docker-003',
    title: 'List Local Images',
    description: 'View all Docker images stored on your local machine.',
    task: 'List all Docker images on your system.',
    hint: 'Use docker images or docker image ls.',
    example: 'docker ps',
    answer: ['docker images', 'docker image ls'],
    tips: [
      'Images are identified by REPOSITORY:TAG',
      'The IMAGE ID is a unique identifier for each image'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker images',
      description: 'List all Docker images stored locally on your system.',
      syntax: 'docker images [OPTIONS] [REPOSITORY[:TAG]]',
      options: [
        { flag: '-a, --all', description: 'Show all images (including intermediate images)' },
        { flag: '-q, --quiet', description: 'Only show image IDs' },
        { flag: '--filter, -f', description: 'Filter output based on conditions' },
        { flag: '--format', description: 'Format output using a Go template' },
        { flag: '--digests', description: 'Show image digests' }
      ],
      examples: [
        { code: 'docker images', description: 'List all local images' },
        { code: 'docker images -q', description: 'List only image IDs' },
        { code: 'docker images nginx', description: 'List all nginx images' },
        { code: 'docker images --filter "dangling=true"', description: 'List dangling (untagged) images' }
      ]
    }
  },
  {
    id: 'docker-004',
    title: 'Run Your First Container',
    description: 'Run a container from an image. The hello-world image is a simple test container.',
    task: 'Run the hello-world container to verify Docker works correctly.',
    hint: 'Use docker run followed by the image name.',
    example: 'docker run ubuntu echo "Hello"',
    answer: ['docker run hello-world'],
    tips: [
      'Docker will pull the image automatically if not present locally',
      'The container runs, outputs a message, and exits'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker run',
      description: 'Create and start a new container from an image.',
      syntax: 'docker run [OPTIONS] IMAGE [COMMAND] [ARG...]',
      options: [
        { flag: '-d, --detach', description: 'Run container in background' },
        { flag: '-it', description: 'Interactive mode with terminal' },
        { flag: '--name', description: 'Assign a name to the container' },
        { flag: '-p, --publish', description: 'Map container port to host port' },
        { flag: '-v, --volume', description: 'Mount a volume' },
        { flag: '-e, --env', description: 'Set environment variables' },
        { flag: '--rm', description: 'Automatically remove container when it exits' }
      ],
      examples: [
        { code: 'docker run hello-world', description: 'Run the hello-world test container' },
        { code: 'docker run -it ubuntu bash', description: 'Run Ubuntu interactively with bash' },
        { code: 'docker run --name myapp -d nginx', description: 'Run nginx in background with a name' }
      ]
    }
  },
  {
    id: 'docker-005',
    title: 'List Running Containers',
    description: 'View containers that are currently running on your system.',
    task: 'Show all currently running Docker containers.',
    hint: 'Use docker ps to list running containers.',
    example: 'docker images',
    answer: ['docker ps', 'docker container ls'],
    tips: [
      'docker ps shows only running containers by default',
      'Add -a to see all containers including stopped ones'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker ps',
      description: 'List running containers on your system.',
      syntax: 'docker ps [OPTIONS]',
      options: [
        { flag: '-a, --all', description: 'Show all containers (including stopped)' },
        { flag: '-q, --quiet', description: 'Only display container IDs' },
        { flag: '-l, --latest', description: 'Show the latest created container' },
        { flag: '-n', description: 'Show n last created containers' },
        { flag: '--filter, -f', description: 'Filter output based on conditions' },
        { flag: '--format', description: 'Format output using a Go template' }
      ],
      examples: [
        { code: 'docker ps', description: 'List all running containers' },
        { code: 'docker ps -a', description: 'List all containers including stopped' },
        { code: 'docker ps -q', description: 'List only container IDs' },
        { code: 'docker ps --filter "status=exited"', description: 'List only stopped containers' }
      ]
    }
  },
  {
    id: 'docker-006',
    title: 'List All Containers',
    description: 'View all containers including stopped ones. This is useful for managing container history.',
    task: 'List all containers, including those that have stopped.',
    hint: 'Add a flag to docker ps to show all containers.',
    example: 'docker ps',
    answer: ['docker ps -a', 'docker ps --all', 'docker container ls -a'],
    tips: [
      'Stopped containers still exist and take up disk space',
      'The STATUS column shows if a container is running or exited'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker ps -a',
      description: 'List all containers on your system, including stopped ones.',
      syntax: 'docker ps -a [OPTIONS]',
      options: [
        { flag: '-a, --all', description: 'Show all containers (default shows just running)' },
        { flag: '-q, --quiet', description: 'Only display container IDs' },
        { flag: '-s, --size', description: 'Display total file sizes' },
        { flag: '--no-trunc', description: 'Do not truncate output' },
        { flag: '--format', description: 'Format output using a Go template' }
      ],
      examples: [
        { code: 'docker ps -a', description: 'Show all containers including stopped' },
        { code: 'docker ps -a -q', description: 'Get IDs of all containers' },
        { code: 'docker ps -a --format "table {{.Names}}\t{{.Status}}"', description: 'Custom format showing name and status' }
      ]
    }
  },
  {
    id: 'docker-007',
    title: 'Stop a Container',
    description: 'Gracefully stop a running container. Docker sends SIGTERM, then SIGKILL after a timeout.',
    task: 'Stop a container named "webserver".',
    hint: 'Use docker stop followed by the container name or ID.',
    example: 'docker stop myapp',
    answer: ['docker stop webserver'],
    tips: [
      'You can use container name or ID',
      'Use docker kill for immediate termination'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker stop',
      description: 'Stop one or more running containers gracefully.',
      syntax: 'docker stop [OPTIONS] CONTAINER [CONTAINER...]',
      options: [
        { flag: '-t, --time', description: 'Seconds to wait before killing (default 10)' },
        { flag: '-s, --signal', description: 'Signal to send to the container' }
      ],
      examples: [
        { code: 'docker stop webserver', description: 'Stop a container named webserver' },
        { code: 'docker stop -t 30 myapp', description: 'Stop with 30 second timeout' },
        { code: 'docker stop $(docker ps -q)', description: 'Stop all running containers' }
      ]
    }
  },
  {
    id: 'docker-008',
    title: 'Remove a Container',
    description: 'Delete a stopped container from your system to free up resources.',
    task: 'Remove a container named "old_app".',
    hint: 'Use docker rm followed by the container name or ID.',
    example: 'docker rm mycontainer',
    answer: ['docker rm old_app'],
    tips: [
      'Container must be stopped before removal (or use -f flag)',
      'Use docker rm -f to force remove a running container'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker rm',
      description: 'Remove one or more stopped containers.',
      syntax: 'docker rm [OPTIONS] CONTAINER [CONTAINER...]',
      options: [
        { flag: '-f, --force', description: 'Force removal of a running container' },
        { flag: '-v, --volumes', description: 'Remove anonymous volumes associated with the container' },
        { flag: '-l, --link', description: 'Remove the specified link' }
      ],
      examples: [
        { code: 'docker rm old_app', description: 'Remove a stopped container' },
        { code: 'docker rm -f running_app', description: 'Force remove a running container' },
        { code: 'docker rm $(docker ps -aq)', description: 'Remove all stopped containers' }
      ]
    }
  },
  {
    id: 'docker-009',
    title: 'Remove an Image',
    description: 'Delete a Docker image from your local system.',
    task: 'Remove the alpine image from your system.',
    hint: 'Use docker rmi followed by the image name.',
    example: 'docker rmi ubuntu',
    answer: ['docker rmi alpine', 'docker image rm alpine'],
    tips: [
      'Cannot remove images used by existing containers',
      'Use -f to force removal'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker rmi',
      description: 'Remove one or more images from the local system.',
      syntax: 'docker rmi [OPTIONS] IMAGE [IMAGE...]',
      options: [
        { flag: '-f, --force', description: 'Force removal of the image' },
        { flag: '--no-prune', description: 'Do not delete untagged parent images' }
      ],
      examples: [
        { code: 'docker rmi alpine', description: 'Remove the alpine image' },
        { code: 'docker rmi nginx:1.21', description: 'Remove a specific tagged image' },
        { code: 'docker rmi -f $(docker images -q)', description: 'Force remove all images' }
      ]
    }
  },
  {
    id: 'docker-010',
    title: 'View Container Logs',
    description: 'Access the output logs of a container. Essential for debugging.',
    task: 'View the logs of a container named "api".',
    hint: 'Use docker logs followed by the container name.',
    example: 'docker logs myapp',
    answer: ['docker logs api'],
    tips: [
      'Add -f to follow logs in real-time (like tail -f)',
      'Add --tail 100 to see only the last 100 lines'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'docker logs',
      description: 'Fetch the logs of a container.',
      syntax: 'docker logs [OPTIONS] CONTAINER',
      options: [
        { flag: '-f, --follow', description: 'Follow log output in real-time' },
        { flag: '--tail', description: 'Number of lines to show from the end' },
        { flag: '-t, --timestamps', description: 'Show timestamps' },
        { flag: '--since', description: 'Show logs since timestamp or relative time' },
        { flag: '--until', description: 'Show logs before timestamp or relative time' }
      ],
      examples: [
        { code: 'docker logs api', description: 'View logs of container named api' },
        { code: 'docker logs -f api', description: 'Follow logs in real-time' },
        { code: 'docker logs --tail 100 api', description: 'Show last 100 lines' },
        { code: 'docker logs --since 1h api', description: 'Show logs from last hour' }
      ]
    }
  },

  // INTERMEDIATE LESSONS (10)
  {
    id: 'docker-011',
    title: 'Run Container in Background',
    description: 'Run a container in detached mode so it runs in the background.',
    task: 'Run an nginx container in the background.',
    hint: 'Use the -d flag for detached mode.',
    example: 'docker run -d ubuntu sleep 1000',
    answer: ['docker run -d nginx'],
    tips: [
      'Detached mode returns the container ID immediately',
      'Use docker logs to see output from detached containers'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker run -d',
      description: 'Run a container in detached (background) mode.',
      syntax: 'docker run -d [OPTIONS] IMAGE [COMMAND]',
      options: [
        { flag: '-d, --detach', description: 'Run container in background and print container ID' },
        { flag: '--name', description: 'Assign a name to the container' },
        { flag: '-p, --publish', description: 'Publish container ports to host' },
        { flag: '--restart', description: 'Restart policy for the container' }
      ],
      examples: [
        { code: 'docker run -d nginx', description: 'Run nginx in background' },
        { code: 'docker run -d --name web nginx', description: 'Run nginx in background with name' },
        { code: 'docker run -d -p 80:80 nginx', description: 'Run nginx in background with port mapping' }
      ]
    }
  },
  {
    id: 'docker-012',
    title: 'Port Mapping',
    description: 'Map a container port to a host port to access services running inside.',
    task: 'Run nginx and map container port 80 to host port 8080.',
    hint: 'Use -p host_port:container_port syntax.',
    example: 'docker run -p 3000:3000 node-app',
    answer: ['docker run -p 8080:80 nginx', 'docker run -d -p 8080:80 nginx'],
    tips: [
      'Format is -p HOST_PORT:CONTAINER_PORT',
      'You can map multiple ports with multiple -p flags'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker run -p',
      description: 'Publish a container port to the host, making services accessible.',
      syntax: 'docker run -p HOST_PORT:CONTAINER_PORT [OPTIONS] IMAGE',
      options: [
        { flag: '-p, --publish', description: 'Publish container port(s) to host' },
        { flag: '-P, --publish-all', description: 'Publish all exposed ports to random ports' },
        { flag: '--expose', description: 'Expose a port without publishing' }
      ],
      examples: [
        { code: 'docker run -p 8080:80 nginx', description: 'Map host 8080 to container 80' },
        { code: 'docker run -p 8080:80 -p 443:443 nginx', description: 'Map multiple ports' },
        { code: 'docker run -p 127.0.0.1:8080:80 nginx', description: 'Bind to specific host interface' },
        { code: 'docker run -P nginx', description: 'Publish all exposed ports randomly' }
      ]
    }
  },
  {
    id: 'docker-013',
    title: 'Execute Command in Container',
    description: 'Run a command inside a running container. Perfect for debugging.',
    task: 'Open an interactive bash shell in a running container named "web".',
    hint: 'Use docker exec with -it flags for interactive terminal.',
    example: 'docker exec -it myapp sh',
    answer: ['docker exec -it web bash', 'docker exec -it web /bin/bash'],
    tips: [
      '-i keeps STDIN open, -t allocates a pseudo-TTY',
      'Use sh instead of bash for alpine-based images'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker exec',
      description: 'Execute a command in a running container.',
      syntax: 'docker exec [OPTIONS] CONTAINER COMMAND [ARG...]',
      options: [
        { flag: '-i, --interactive', description: 'Keep STDIN open' },
        { flag: '-t, --tty', description: 'Allocate a pseudo-TTY' },
        { flag: '-d, --detach', description: 'Run command in background' },
        { flag: '-e, --env', description: 'Set environment variables' },
        { flag: '-w, --workdir', description: 'Working directory inside container' },
        { flag: '-u, --user', description: 'Username or UID to run command as' }
      ],
      examples: [
        { code: 'docker exec -it web bash', description: 'Open interactive bash shell' },
        { code: 'docker exec web ls /app', description: 'Run ls command in container' },
        { code: 'docker exec -e DEBUG=1 web npm test', description: 'Run command with env variable' },
        { code: 'docker exec -u root web apt update', description: 'Run command as root user' }
      ]
    }
  },
  {
    id: 'docker-014',
    title: 'Build an Image',
    description: 'Build a Docker image from a Dockerfile in the current directory.',
    task: 'Build an image and tag it as "myapp:v1".',
    hint: 'Use docker build with -t for tagging.',
    example: 'docker build -t webapp .',
    answer: ['docker build -t myapp:v1 .'],
    tips: [
      'The . specifies the build context (current directory)',
      'Dockerfile must be in the build context'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker build',
      description: 'Build a Docker image from a Dockerfile.',
      syntax: 'docker build [OPTIONS] PATH | URL | -',
      options: [
        { flag: '-t, --tag', description: 'Name and optionally tag the image (name:tag)' },
        { flag: '-f, --file', description: 'Name of the Dockerfile (default: PATH/Dockerfile)' },
        { flag: '--no-cache', description: 'Do not use cache when building' },
        { flag: '--build-arg', description: 'Set build-time variables' },
        { flag: '--target', description: 'Set target build stage' },
        { flag: '--platform', description: 'Set platform for build (linux/amd64, etc.)' }
      ],
      examples: [
        { code: 'docker build -t myapp:v1 .', description: 'Build and tag image from current directory' },
        { code: 'docker build -f Dockerfile.prod -t myapp:prod .', description: 'Build using specific Dockerfile' },
        { code: 'docker build --no-cache -t myapp .', description: 'Build without using cache' },
        { code: 'docker build --build-arg VERSION=1.0 -t myapp .', description: 'Build with build argument' }
      ]
    }
  },
  {
    id: 'docker-015',
    title: 'Create a Volume',
    description: 'Volumes persist data outside of containers. Create a named volume.',
    task: 'Create a Docker volume named "database_data".',
    hint: 'Use docker volume create.',
    example: 'docker volume create mydata',
    answer: ['docker volume create database_data'],
    tips: [
      'Volumes survive container removal',
      'List volumes with docker volume ls'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker volume create',
      description: 'Create a named volume for persistent data storage.',
      syntax: 'docker volume create [OPTIONS] [VOLUME]',
      options: [
        { flag: '-d, --driver', description: 'Specify volume driver (default: local)' },
        { flag: '--label', description: 'Set metadata for a volume' },
        { flag: '-o, --opt', description: 'Set driver specific options' }
      ],
      examples: [
        { code: 'docker volume create database_data', description: 'Create a named volume' },
        { code: 'docker volume create --label env=prod mydata', description: 'Create volume with label' },
        { code: 'docker volume ls', description: 'List all volumes' },
        { code: 'docker volume inspect mydata', description: 'View volume details' }
      ]
    }
  },
  {
    id: 'docker-016',
    title: 'Run with Volume Mount',
    description: 'Mount a volume to persist data across container restarts.',
    task: 'Run a postgres container with the "pgdata" volume mounted to /var/lib/postgresql/data.',
    hint: 'Use -v volume_name:container_path syntax.',
    example: 'docker run -v mydata:/app/data myimage',
    answer: ['docker run -v pgdata:/var/lib/postgresql/data postgres', 'docker run -d -v pgdata:/var/lib/postgresql/data postgres'],
    tips: [
      'Named volumes are managed by Docker',
      'You can also bind mount host directories'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker run -v',
      description: 'Mount a volume or bind mount to a container for data persistence.',
      syntax: 'docker run -v VOLUME_NAME:CONTAINER_PATH[:OPTIONS] IMAGE',
      options: [
        { flag: '-v, --volume', description: 'Bind mount a volume (name:path or hostpath:containerpath)' },
        { flag: '--mount', description: 'Attach a filesystem mount (more explicit syntax)' },
        { flag: ':ro', description: 'Mount as read-only (append to path)' }
      ],
      examples: [
        { code: 'docker run -v pgdata:/var/lib/postgresql/data postgres', description: 'Mount named volume' },
        { code: 'docker run -v /host/path:/container/path nginx', description: 'Bind mount host directory' },
        { code: 'docker run -v mydata:/data:ro nginx', description: 'Mount volume as read-only' },
        { code: 'docker run --mount source=mydata,target=/data nginx', description: 'Mount using --mount syntax' }
      ]
    }
  },
  {
    id: 'docker-017',
    title: 'Create a Network',
    description: 'Create a custom network for container communication.',
    task: 'Create a bridge network named "app_network".',
    hint: 'Use docker network create.',
    example: 'docker network create mynet',
    answer: ['docker network create app_network'],
    tips: [
      'Containers on the same network can communicate by name',
      'Default bridge network does not support name resolution'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker network create',
      description: 'Create a custom Docker network for container communication.',
      syntax: 'docker network create [OPTIONS] NETWORK',
      options: [
        { flag: '-d, --driver', description: 'Driver to manage the network (bridge, overlay, etc.)' },
        { flag: '--subnet', description: 'Subnet in CIDR format' },
        { flag: '--gateway', description: 'IPv4 or IPv6 Gateway for the subnet' },
        { flag: '--ip-range', description: 'Allocate container IP from a sub-range' },
        { flag: '--internal', description: 'Restrict external access to the network' }
      ],
      examples: [
        { code: 'docker network create app_network', description: 'Create a bridge network' },
        { code: 'docker network create -d overlay mynet', description: 'Create an overlay network' },
        { code: 'docker network create --subnet=172.20.0.0/16 mynet', description: 'Create network with custom subnet' },
        { code: 'docker run --network app_network nginx', description: 'Connect container to network' }
      ]
    }
  },
  {
    id: 'docker-018',
    title: 'Docker Compose Up',
    description: 'Start services defined in docker-compose.yml in detached mode.',
    task: 'Start all services defined in docker-compose.yml in the background.',
    hint: 'Use docker-compose up with detached flag.',
    example: 'docker-compose up',
    answer: ['docker-compose up -d', 'docker compose up -d'],
    tips: [
      'docker-compose reads docker-compose.yml by default',
      'Use -f to specify a different compose file'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker-compose up',
      description: 'Build, create, start, and attach to containers defined in docker-compose.yml.',
      syntax: 'docker-compose up [OPTIONS] [SERVICE...]',
      options: [
        { flag: '-d, --detach', description: 'Run containers in the background' },
        { flag: '--build', description: 'Build images before starting containers' },
        { flag: '--force-recreate', description: 'Recreate containers even if unchanged' },
        { flag: '--no-deps', description: 'Do not start linked services' },
        { flag: '--scale', description: 'Scale SERVICE to NUM instances' },
        { flag: '-f, --file', description: 'Specify alternate compose file' }
      ],
      examples: [
        { code: 'docker-compose up -d', description: 'Start all services in background' },
        { code: 'docker compose up -d --build', description: 'Build and start services' },
        { code: 'docker-compose up -d web db', description: 'Start only web and db services' },
        { code: 'docker-compose -f docker-compose.prod.yml up -d', description: 'Use alternate compose file' }
      ]
    }
  },
  {
    id: 'docker-019',
    title: 'Docker Compose Down',
    description: 'Stop and remove containers, networks created by docker-compose.',
    task: 'Stop and remove all containers and networks from the compose project.',
    hint: 'Use docker-compose down.',
    example: 'docker-compose stop',
    answer: ['docker-compose down', 'docker compose down'],
    tips: [
      'Add -v to also remove volumes',
      'Add --rmi all to remove images too'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker-compose down',
      description: 'Stop and remove containers, networks, images, and volumes created by up.',
      syntax: 'docker-compose down [OPTIONS]',
      options: [
        { flag: '-v, --volumes', description: 'Remove named volumes declared in the compose file' },
        { flag: '--rmi all', description: 'Remove all images used by services' },
        { flag: '--rmi local', description: 'Remove only locally built images' },
        { flag: '--remove-orphans', description: 'Remove containers for undefined services' },
        { flag: '-t, --timeout', description: 'Shutdown timeout in seconds (default 10)' }
      ],
      examples: [
        { code: 'docker-compose down', description: 'Stop and remove containers and networks' },
        { code: 'docker-compose down -v', description: 'Also remove volumes' },
        { code: 'docker-compose down --rmi all', description: 'Also remove all images' },
        { code: 'docker compose down --remove-orphans', description: 'Remove orphaned containers' }
      ]
    }
  },
  {
    id: 'docker-020',
    title: 'Tag an Image',
    description: 'Add a new tag to an existing image, useful for versioning.',
    task: 'Tag the image "myapp:latest" as "myapp:v2.0".',
    hint: 'Use docker tag source_image target_image.',
    example: 'docker tag myimage myimage:v1',
    answer: ['docker tag myapp:latest myapp:v2.0', 'docker tag myapp myapp:v2.0'],
    tips: [
      'Tags are references to images, not copies',
      'Common tags: latest, version numbers, git commit hashes'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'docker tag',
      description: 'Create a tag TARGET_IMAGE that refers to SOURCE_IMAGE.',
      syntax: 'docker tag SOURCE_IMAGE[:TAG] TARGET_IMAGE[:TAG]',
      options: [],
      examples: [
        { code: 'docker tag myapp:latest myapp:v2.0', description: 'Add version tag to image' },
        { code: 'docker tag myapp myuser/myapp:latest', description: 'Tag for Docker Hub push' },
        { code: 'docker tag myapp registry.example.com/myapp:v1', description: 'Tag for private registry' },
        { code: 'docker tag abc123 myapp:stable', description: 'Tag image by ID' }
      ]
    }
  },

  // ADVANCED LESSONS (5)
  {
    id: 'docker-021',
    title: 'System Cleanup',
    description: 'Remove all unused containers, networks, images, and optionally volumes.',
    task: 'Clean up all unused Docker objects (containers, networks, images) without removing volumes.',
    hint: 'Use docker system prune.',
    example: 'docker container prune',
    answer: ['docker system prune', 'docker system prune -f'],
    tips: [
      'Add -a to also remove unused images (not just dangling)',
      'Add --volumes to also remove unused volumes'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'docker system prune',
      description: 'Remove unused data including stopped containers, unused networks, dangling images, and build cache.',
      syntax: 'docker system prune [OPTIONS]',
      options: [
        { flag: '-a, --all', description: 'Remove all unused images, not just dangling ones' },
        { flag: '-f, --force', description: 'Do not prompt for confirmation' },
        { flag: '--volumes', description: 'Also prune volumes' },
        { flag: '--filter', description: 'Provide filter values (e.g., until=24h)' }
      ],
      examples: [
        { code: 'docker system prune', description: 'Remove unused containers, networks, images' },
        { code: 'docker system prune -a', description: 'Also remove all unused images' },
        { code: 'docker system prune -af --volumes', description: 'Full cleanup including volumes' },
        { code: 'docker system prune --filter "until=24h"', description: 'Remove items older than 24 hours' }
      ]
    }
  },
  {
    id: 'docker-022',
    title: 'Monitor Container Resources',
    description: 'View real-time resource usage statistics for running containers.',
    task: 'Display live resource usage stats for all running containers.',
    hint: 'Use docker stats.',
    example: 'docker top mycontainer',
    answer: ['docker stats'],
    tips: [
      'Shows CPU%, memory, network I/O, disk I/O',
      'Specify container names to limit output'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'docker stats',
      description: 'Display a live stream of container resource usage statistics.',
      syntax: 'docker stats [OPTIONS] [CONTAINER...]',
      options: [
        { flag: '-a, --all', description: 'Show all containers (default shows just running)' },
        { flag: '--no-stream', description: 'Disable streaming stats and only pull the first result' },
        { flag: '--no-trunc', description: 'Do not truncate output' },
        { flag: '--format', description: 'Format output using a Go template' }
      ],
      examples: [
        { code: 'docker stats', description: 'Show live stats for all running containers' },
        { code: 'docker stats web db', description: 'Show stats for specific containers' },
        { code: 'docker stats --no-stream', description: 'Show stats once without streaming' },
        { code: 'docker stats --format "table {{.Name}}\t{{.CPUPerc}}\t{{.MemUsage}}"', description: 'Custom format output' }
      ]
    }
  },
  {
    id: 'docker-023',
    title: 'Inspect Container Details',
    description: 'Get detailed information about a container in JSON format.',
    task: 'Get detailed information about a container named "production_api".',
    hint: 'Use docker inspect.',
    example: 'docker inspect mycontainer',
    answer: ['docker inspect production_api'],
    tips: [
      'Returns JSON with all container metadata',
      'Use --format to extract specific fields'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'docker inspect',
      description: 'Return low-level information on Docker objects in JSON format.',
      syntax: 'docker inspect [OPTIONS] NAME|ID [NAME|ID...]',
      options: [
        { flag: '-f, --format', description: 'Format output using a Go template' },
        { flag: '-s, --size', description: 'Display total file sizes for containers' },
        { flag: '--type', description: 'Return JSON for specified type (container, image, volume, network)' }
      ],
      examples: [
        { code: 'docker inspect production_api', description: 'Get full JSON details of container' },
        { code: 'docker inspect --format "{{.State.Status}}" web', description: 'Get container status' },
        { code: 'docker inspect --format "{{.NetworkSettings.IPAddress}}" web', description: 'Get container IP address' },
        { code: 'docker inspect --type image nginx', description: 'Inspect an image' }
      ]
    }
  },
  {
    id: 'docker-024',
    title: 'Export and Import Images',
    description: 'Save an image to a tar archive for offline transfer.',
    task: 'Save the "webapp:v1" image to a file called "webapp.tar".',
    hint: 'Use docker save with -o flag for output file.',
    example: 'docker save -o myimage.tar myimage:tag',
    answer: ['docker save -o webapp.tar webapp:v1'],
    tips: [
      'Use docker load to import the image later',
      'Useful for air-gapped environments'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'docker save',
      description: 'Save one or more images to a tar archive file.',
      syntax: 'docker save [OPTIONS] IMAGE [IMAGE...]',
      options: [
        { flag: '-o, --output', description: 'Write to a file instead of STDOUT' }
      ],
      examples: [
        { code: 'docker save -o webapp.tar webapp:v1', description: 'Save image to tar file' },
        { code: 'docker save webapp:v1 | gzip > webapp.tar.gz', description: 'Save and compress image' },
        { code: 'docker save -o backup.tar img1 img2 img3', description: 'Save multiple images' },
        { code: 'docker load -i webapp.tar', description: 'Load image from tar file' }
      ]
    }
  },
  {
    id: 'docker-025',
    title: 'Push to Registry',
    description: 'Push a local image to a Docker registry like Docker Hub.',
    task: 'Push the image "myuser/webapp:v1" to Docker Hub.',
    hint: 'Use docker push with the full image name.',
    example: 'docker push username/image:tag',
    answer: ['docker push myuser/webapp:v1'],
    tips: [
      'You must be logged in with docker login first',
      'Image name must include your Docker Hub username'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'docker push',
      description: 'Upload an image to a registry.',
      syntax: 'docker push [OPTIONS] NAME[:TAG]',
      options: [
        { flag: '-a, --all-tags', description: 'Push all tagged images in the repository' },
        { flag: '--disable-content-trust', description: 'Skip image signing' },
        { flag: '-q, --quiet', description: 'Suppress verbose output' }
      ],
      examples: [
        { code: 'docker push myuser/webapp:v1', description: 'Push image to Docker Hub' },
        { code: 'docker push -a myuser/webapp', description: 'Push all tags of an image' },
        { code: 'docker push registry.example.com/myapp:v1', description: 'Push to private registry' },
        { code: 'docker login && docker push myuser/app', description: 'Login then push' }
      ]
    }
  },

  // EXPERT LESSONS (5)
  {
    id: 'docker-026',
    title: 'Debug Container Networking',
    description: 'Inspect network settings of a container to troubleshoot connectivity issues.',
    task: 'Display the IP address of a container named "backend" using inspect and format.',
    hint: 'Use docker inspect with --format to extract the IP address.',
    example: 'docker inspect --format "{{.State.Status}}" mycontainer',
    answer: [
      "docker inspect --format '{{range .NetworkSettings.Networks}}{{.IPAddress}}{{end}}' backend",
      "docker inspect --format \"{{range .NetworkSettings.Networks}}{{.IPAddress}}{{end}}\" backend",
      'docker inspect -f \'{{range .NetworkSettings.Networks}}{{.IPAddress}}{{end}}\' backend'
    ],
    tips: [
      'The format uses Go template syntax',
      'Network settings vary based on network type'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'docker inspect --format',
      description: 'Extract specific information from Docker objects using Go template syntax.',
      syntax: 'docker inspect --format "TEMPLATE" NAME|ID',
      options: [
        { flag: '-f, --format', description: 'Format output using a Go template' },
        { flag: '{{.Field}}', description: 'Access object field' },
        { flag: '{{range}}{{end}}', description: 'Iterate over arrays/maps' },
        { flag: '{{json .}}', description: 'Output as JSON' }
      ],
      examples: [
        { code: "docker inspect --format '{{range .NetworkSettings.Networks}}{{.IPAddress}}{{end}}' backend", description: 'Get container IP address' },
        { code: "docker inspect --format '{{.State.Pid}}' web", description: 'Get container process ID' },
        { code: "docker inspect --format '{{json .Config.Env}}' web", description: 'Get environment variables as JSON' },
        { code: "docker inspect --format '{{.HostConfig.PortBindings}}' web", description: 'Get port bindings' }
      ]
    }
  },
  {
    id: 'docker-027',
    title: 'Set Resource Limits',
    description: 'Limit CPU and memory resources for a container to prevent resource exhaustion.',
    task: 'Run an nginx container with a maximum of 512MB memory and 0.5 CPU.',
    hint: 'Use --memory and --cpus flags.',
    example: 'docker run --memory="256m" myimage',
    answer: [
      'docker run --memory=512m --cpus=0.5 nginx',
      'docker run -m 512m --cpus=0.5 nginx',
      'docker run -d --memory=512m --cpus=0.5 nginx'
    ],
    tips: [
      'Memory can be specified in b, k, m, g units',
      'CPUs can be decimal (0.5 = half a CPU core)'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'docker run --memory --cpus',
      description: 'Run a container with CPU and memory resource constraints.',
      syntax: 'docker run --memory=LIMIT --cpus=LIMIT [OPTIONS] IMAGE',
      options: [
        { flag: '-m, --memory', description: 'Memory limit (e.g., 512m, 2g)' },
        { flag: '--memory-swap', description: 'Total memory + swap limit (-1 for unlimited swap)' },
        { flag: '--cpus', description: 'Number of CPUs (e.g., 0.5, 2)' },
        { flag: '--cpu-shares', description: 'CPU shares (relative weight)' },
        { flag: '--cpuset-cpus', description: 'CPUs in which to allow execution (0-3, 0,1)' },
        { flag: '--memory-reservation', description: 'Memory soft limit' }
      ],
      examples: [
        { code: 'docker run --memory=512m --cpus=0.5 nginx', description: 'Limit to 512MB RAM and half a CPU' },
        { code: 'docker run -m 1g --memory-swap=2g myapp', description: 'Set memory and swap limits' },
        { code: 'docker run --cpus=2 --cpuset-cpus="0,1" myapp', description: 'Use 2 CPUs on cores 0 and 1' },
        { code: 'docker update --memory=1g mycontainer', description: 'Update limits on running container' }
      ]
    }
  },
  {
    id: 'docker-028',
    title: 'Health Checks',
    description: 'Configure a health check to monitor container health.',
    task: 'Run an nginx container with a health check that curls localhost every 30 seconds.',
    hint: 'Use --health-cmd and --health-interval flags.',
    example: 'docker run --health-cmd="wget -q --spider http://localhost" myimage',
    answer: [
      'docker run --health-cmd="curl -f http://localhost" --health-interval=30s nginx',
      'docker run -d --health-cmd="curl -f http://localhost" --health-interval=30s nginx'
    ],
    tips: [
      'Health status shown in docker ps output',
      'Container marked unhealthy after 3 consecutive failures'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'docker run --health-cmd',
      description: 'Configure a health check command to monitor container health status.',
      syntax: 'docker run --health-cmd="COMMAND" [OPTIONS] IMAGE',
      options: [
        { flag: '--health-cmd', description: 'Command to run to check health' },
        { flag: '--health-interval', description: 'Time between checks (default 30s)' },
        { flag: '--health-timeout', description: 'Maximum time for check to run (default 30s)' },
        { flag: '--health-retries', description: 'Consecutive failures needed to report unhealthy (default 3)' },
        { flag: '--health-start-period', description: 'Start period for container to initialize' },
        { flag: '--no-healthcheck', description: 'Disable any container-specified healthcheck' }
      ],
      examples: [
        { code: 'docker run --health-cmd="curl -f http://localhost" --health-interval=30s nginx', description: 'Health check with curl every 30s' },
        { code: 'docker run --health-cmd="pg_isready" --health-interval=10s postgres', description: 'PostgreSQL health check' },
        { code: 'docker run --health-cmd="redis-cli ping" --health-retries=5 redis', description: 'Redis health check with 5 retries' },
        { code: 'docker inspect --format "{{.State.Health.Status}}" web', description: 'Check health status' }
      ]
    }
  },
  {
    id: 'docker-029',
    title: 'Copy Files Between Host and Container',
    description: 'Copy files or directories between a container and the local filesystem.',
    task: 'Copy the file "/app/config.json" from container "api" to your current directory.',
    hint: 'Use docker cp with container:path syntax.',
    example: 'docker cp mycontainer:/var/log/app.log ./logs/',
    answer: ['docker cp api:/app/config.json .', 'docker cp api:/app/config.json ./'],
    tips: [
      'Works with running or stopped containers',
      'Can copy directories recursively'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'docker cp',
      description: 'Copy files or directories between a container and the local filesystem.',
      syntax: 'docker cp [OPTIONS] CONTAINER:SRC_PATH DEST_PATH\ndocker cp [OPTIONS] SRC_PATH CONTAINER:DEST_PATH',
      options: [
        { flag: '-a, --archive', description: 'Archive mode (copy all uid/gid information)' },
        { flag: '-L, --follow-link', description: 'Always follow symbol link in SRC_PATH' },
        { flag: '-q, --quiet', description: 'Suppress progress output' }
      ],
      examples: [
        { code: 'docker cp api:/app/config.json .', description: 'Copy file from container to current directory' },
        { code: 'docker cp ./config.json api:/app/', description: 'Copy file from host to container' },
        { code: 'docker cp api:/var/log/ ./logs/', description: 'Copy entire directory from container' },
        { code: 'docker cp -a api:/app/data ./backup/', description: 'Copy preserving permissions' }
      ]
    }
  },
  {
    id: 'docker-030',
    title: 'Container Restart Policies',
    description: 'Configure automatic restart behavior for containers.',
    task: 'Run a redis container that automatically restarts unless manually stopped.',
    hint: 'Use the --restart flag with the appropriate policy.',
    example: 'docker run --restart=always myimage',
    answer: [
      'docker run --restart=unless-stopped redis',
      'docker run -d --restart=unless-stopped redis'
    ],
    tips: [
      'Policies: no, on-failure, always, unless-stopped',
      'unless-stopped: restarts except when manually stopped'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'docker run --restart',
      description: 'Configure the restart policy for a container to control automatic restarts.',
      syntax: 'docker run --restart=POLICY [OPTIONS] IMAGE',
      options: [
        { flag: '--restart=no', description: 'Do not automatically restart (default)' },
        { flag: '--restart=on-failure[:max-retries]', description: 'Restart only if container exits with non-zero status' },
        { flag: '--restart=always', description: 'Always restart regardless of exit status' },
        { flag: '--restart=unless-stopped', description: 'Always restart unless explicitly stopped' }
      ],
      examples: [
        { code: 'docker run --restart=unless-stopped redis', description: 'Restart unless manually stopped' },
        { code: 'docker run --restart=always nginx', description: 'Always restart container' },
        { code: 'docker run --restart=on-failure:5 myapp', description: 'Restart on failure up to 5 times' },
        { code: 'docker update --restart=always mycontainer', description: 'Update restart policy on existing container' }
      ]
    }
  }
];

export default dockerLessons;
